// File: LineChartMUI.jsx
import React, { useMemo, useState } from "react";
import { useMediaQuery, Box, Typography, Grid, ToggleButton } from "@mui/material";
import { useTranslation } from "react-i18next";
import { LineChart } from "@mui/x-charts";
import { red, green, grey } from "@mui/material/colors";

export default function LineChartMUI({ data, selectedMonths }) {
	const { t } = useTranslation();
	const isLargeScreen = useMediaQuery("(min-width:1024px) and (min-height:600px)");

	// Ensure months are numbers
	const selMonths = useMemo(
		() =>
			selectedMonths.map((m) =>
				typeof m === "string" && m.includes("-")
					? Number(m.split("-")[1]) // extrae el mes: "2025-11" → 11
					: Number(m)
			),
		[selectedMonths]
	);

	// Normalize data into an array of outputs
	const outputs = useMemo(() => (data ? (Array.isArray(data) ? data : [data]) : []), [data]);

	// Main data points: demand only from first output, personas from each
	const points = useMemo(() => {
		const tmp = [];
		outputs.forEach((out, idx) => {
			Object.keys(out).forEach((monthKey) => {
				if (monthKey === "simulacion_id" || !out[monthKey].personas_activas) return;
				const monthNum = Number(monthKey);
				if (selMonths.length > 0 && !selMonths.includes(monthNum)) return;

				const monthData = out[monthKey];
				Object.entries(monthData.personas_activas).forEach(([ts, personas]) => {
					const demandVal = monthData.demanda?.[ts] ?? 0;

					const dayFull = ts.split("T")[0];
					const monthDay = dayFull;
					tmp.push({
						timestamp: ts,
						monthDay,
						personas,
						demanda: idx === 0 ? demandVal : 0,
						source: idx + 1,
					});
				});
			});
		});
		return tmp.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
	}, [outputs, selMonths]);

	// Aggregate daily for main chart
	const aggregatedDays = useMemo(() => {
		const grouped = {};
		points.forEach(({ monthDay, demanda, personas, source }) => {
			if (!grouped[monthDay]) grouped[monthDay] = { monthDay, demanda: 0, personas1: 0, personas2: 0 };
			grouped[monthDay].demanda += demanda;
			if (source === 1) grouped[monthDay].personas1 += personas;
			if (source === 2) grouped[monthDay].personas2 += personas;
		});
		return Object.values(grouped).sort((a, b) => a.monthDay.localeCompare(b.monthDay));
	}, [points]);
	const hasRealData = useMemo(() => aggregatedDays.some((d) => d.personas2 > 0), [aggregatedDays]);

	const mainSeries = [
		{
			type: "line",
			dataKey: "demanda",
			label: t("charts.demanda"),
			markerSize: 8,
			markers: "all",
			color: red[500],
		},
		{
			type: "line",
			dataKey: "personas1",
			label: t("charts.personas_sugerida"),
			markerSize: 8,
			markers: "all",
			color: green[500],
		},
		...(hasRealData
			? [
					{
						type: "line",
						dataKey: "personas2",
						label: t("charts.personas_actual"),
						markerSize: 8,
						markers: "all",
						color: grey[500],
					},
			  ]
			: []),
	];
	// Days available for detail chart
	const availableDays = useMemo(() => aggregatedDays.map((d) => d.monthDay), [aggregatedDays]);

	// Selected days state
	const [selectedDays, setSelectedDays] = useState([]);
	const selDaysSet = useMemo(() => new Set(selectedDays), [selectedDays]);

	// Detail data grouped by timestamp
	const detailData = useMemo(() => {
		const grp = {};
		points.forEach(({ timestamp, monthDay, demanda, personas, source }) => {
			if (!selDaysSet.has(monthDay)) return;
			if (!grp[timestamp]) grp[timestamp] = { timestamp, demanda: 0, personas1: 0, personas2: 0 };
			if (source === 1) {
				grp[timestamp].demanda += demanda;
				grp[timestamp].personas1 += personas;
			}
			if (source === 2) grp[timestamp].personas2 += personas;
		});
		return Object.values(grp)
			.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
			.map(({ timestamp, demanda, personas1, personas2 }) => ({
				dateLabel: `${timestamp.split("T")[0].substring(5)} ${timestamp.substring(11, 16)}`,
				demanda,
				personas1,
				personas2,
			}));
	}, [points, selDaysSet]);
	const hasRealDataD = useMemo(() => detailData.some((d) => d.personas2 > 0), [detailData]);

	const detailSeries = [
		{
			type: "line",
			dataKey: "demanda",
			label: t("charts.demanda"),
			markerSize: 8,
			markers: "all",
			color: red[500],
		},
		{
			type: "line",
			dataKey: "personas1",
			label: t("charts.personas_sugerida"),
			markerSize: 8,
			markers: "all",
			color: green[500],
		},
		...(hasRealDataD
			? [
					{
						type: "line",
						dataKey: "personas2",
						label: t("charts.personas_actual"),
						markerSize: 8,
						markers: "all",
						color: grey[500],
					},
			  ]
			: []),
	];
	const [showAllDays, setShowAllDays] = useState(false);
	const DAYS_IN_FIRST_LINE = isLargeScreen ? 22 : 10;
	const visibleDays = showAllDays ? availableDays : availableDays.slice(0, DAYS_IN_FIRST_LINE);

	return (
		<Box sx={{ width: "100%", mb: 2 }}>
			<Grid container spacing={2}>
				{/* Main Line Chart */}
				<Grid item xs={12}>
					<Typography variant="subtitle2" align="center" sx={{ mb: 1 }}>
						{t("charts.fore_cobertura")}
					</Typography>
					{aggregatedDays.length > 0 ? (
						<LineChart
							dataset={aggregatedDays}
							height={300}
							xAxis={[
								{
									dataKey: "monthDay",
									scaleType: "band",
									tickFormatter: (val) => {
										const d = new Date(val);
										return d.toLocaleDateString("es-CL", {
											day: "2-digit",
											month: "short",
											year: "numeric",
										});
									},
								},
							]}
							yAxis={[{ min: 0 }]}
							series={mainSeries}
							slotProps={{
								legend: {
									labelStyle: { fontSize: 12 },
									direction: "row",
									position: { vertical: "top", horizontal: "middle" },
									padding: -5,
									itemMarkWidth: 10,
									itemMarkHeight: 10,
									markGap: 7,
									itemGap: 7,
								},
							}}
							grid={{ horizontal: true, vertical: false }}
							margin={{ top: 20, right: 20, bottom: 20, left: 30 }}
						/>
					) : (
						<Typography align="center" color="error">
							{t("new_charts.no_data")}
						</Typography>
					)}
				</Grid>

				{/* Day Toggle Buttons */}
				<Grid item xs={12}>
					<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, justifyContent: "center" }}>
						{visibleDays.map((day) => (
							<ToggleButton
								key={day}
								value={day}
								selected={selectedDays.includes(day)}
								onChange={() =>
									setSelectedDays((prev) =>
										prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
									)
								}
								sx={{
									padding: "2px 4px",
									minWidth: "30px",
									fontSize: "0.7rem",
								}}
							>
								{day}
							</ToggleButton>
						))}
					</Box>
					{availableDays.length > DAYS_IN_FIRST_LINE && (
						<Typography
							variant="body2"
							align="center"
							sx={{ mt: 1, cursor: "pointer" }}
							onClick={() => setShowAllDays((show) => !show)}
						>
							{showAllDays ? t("new_charts.show_less_days") : t("new_charts.show_more_days")}
						</Typography>
					)}
				</Grid>

				{/* Detail Line Chart */}
				{selectedDays.length > 0 && (
					<Grid item xs={12}>
						<Typography variant="subtitle2" align="center" sx={{ mb: 1 }}>
							{t("new_charts.detail_by_hour")}
						</Typography>
						{detailData.length > 0 ? (
							<LineChart
								dataset={detailData}
								height={300}
								xAxis={[{ dataKey: "dateLabel", scaleType: "point" }]}
								yAxis={[{ min: 0 }]}
								series={detailSeries}
								slotProps={{
									legend: {
										labelStyle: { fontSize: 12 },
										direction: "row",
										position: { vertical: "top", horizontal: "middle" },
										padding: -5,
										itemMarkWidth: 10,
										itemMarkHeight: 10,
										markGap: 7,
										itemGap: 7,
									},
								}}
								grid={{ horizontal: true, vertical: false }}
								margin={{ top: 20, right: 20, bottom: 20, left: 30 }}
							/>
						) : (
							<Typography align="center">{t("new_charts.no_data")} </Typography>
						)}
					</Grid>
				)}
			</Grid>
		</Box>
	);
}

// // CoverageCard: shows % coverage for each output
// function CoverageCard({ outputs, selMonths }) {
// 	const { t } = useTranslation();
// 	const values = outputs.map((out) => {
// 		const arr = Object.keys(out)
// 			.filter((k) => k !== "simulacion_id")
// 			.filter((k) => selMonths.length === 0 || selMonths.includes(Number(k)))
// 			.map((k) => out[k].porcentaje_cobertura)
// 			.filter((v) => typeof v === "number");
// 		return arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;
// 	});
// 	return (
// 		<Card
// 			sx={{
// 				mx: 1,
// 				display: "flex",
// 				justifyContent: "center",
// 				alignItems: "center",
// 			}}
// 		>
// 			<CardContent>
// 				<Typography variant="h6" align="center">
// 					{t("aux.coverage")} %
// 				</Typography>
// 				<Typography variant="subtitle2" align="center">
// 					{t("charts.personas_sugerida")}: {values[0]?.toFixed(2)}%
// 				</Typography>
// 				{values[1] != null && (
// 					<Typography variant="subtitle2" align="center">
// 						{t("charts.personas_actual")}: {values[1].toFixed(2)}%
// 					</Typography>
// 				)}
// 			</CardContent>
// 		</Card>
// 	);
// }

// // HolguraCard: shows % slack (holgura) for each output
// function HolguraCard({ outputs, selMonths }) {
// 	const { t } = useTranslation();
// 	const values = outputs.map((out) => {
// 		const arr = Object.keys(out)
// 			.filter((k) => k !== "simulacion_id")
// 			.filter((k) => selMonths.length === 0 || selMonths.includes(Number(k)))
// 			.map((k) => out[k].porcentaje_holgura)
// 			.filter((v) => typeof v === "number");
// 		return arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;
// 	});
// 	return (
// 		<Card
// 			sx={{
// 				mx: 1,

// 				display: "flex",
// 				justifyContent: "center",
// 				alignItems: "center",
// 			}}
// 		>
// 			<CardContent>
// 				<Typography variant="h6" align="center">
// 					{t("aux.holgura")} %
// 				</Typography>
// 				<Typography variant="subtitle2" align="center">
// 					{t("charts.personas_sugerida")}: {values[0]?.toFixed(2)}%
// 				</Typography>
// 				{values[1] != null && (
// 					<Typography variant="subtitle2" align="center">
// 						{t("charts.personas_actual")}: {values[1].toFixed(2)}%
// 					</Typography>
// 				)}
// 			</CardContent>
// 		</Card>
// 	);
// }
